import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from './useAuthContext';
import {
  getAllNotificationCounts,
  updateLastVisitTime,
  getUserLastVisitTimes,
  initializeUserVisitTimes
} from '../services/notificationService';



/**
 * Custom hook for managing notification badges and counts
 * @returns {Object} Notification state and functions
 */
export const useNotifications = () => {
  const { user } = useAuthContext();
  const [counts, setCounts] = useState({
    opportunities: 0,
    people: 0,
    forum: 0
  });
  const [newItems, setNewItems] = useState({
    opportunities: [],
    people: [],
    forum: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastVisitTimes, setLastVisitTimes] = useState(null);

  // Debounce timer ref for preventing excessive API calls
  const debounceTimerRef = useRef(null);
  
  // Focus-based refresh configuration
  const focusRefreshEnabled = useRef(true); // Can be made configurable
  const lastFocusRefresh = useRef(Date.now());

  /**
   * Load notification counts with enhanced error handling and retry logic
   */
  const loadNotificationCounts = useCallback(async (retryCount = 0) => {
    if (!user?.uid) {
      setLoading(false);
      return { success: false, error: 'No user ID' };
    }

    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    try {
      setLoading(true);
      setError(null);

      // Initialize visit times if needed
      await initializeUserVisitTimes(user.uid);

      // Get current counts and new items
      const result = await getAllNotificationCounts(user.uid);
      
      if (result.success) {
        // Validate enhanced data structure
        if (result.newItems && typeof result.newItems === 'object') {
          setCounts(result.counts);
          setNewItems(result.newItems);
        } else {
          setCounts(result.counts);
          setNewItems({ opportunities: [], people: [], forum: [] });
        }
        
        setError(null);
        setIsRetrying(false);
      } else {
        
        // Implement retry logic with exponential backoff
        if (retryCount < 3 && (result.error?.includes('network') || result.error?.includes('timeout'))) {
          const retryDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
          
          setIsRetrying(true);
          setTimeout(() => {
            loadNotificationCounts(retryCount + 1);
          }, retryDelay);
          return { success: false, error: result.error, retrying: true };
        }
        
        setError(result.error);
        setCounts({ opportunities: 0, people: 0, forum: 0 });
        setNewItems({ opportunities: [], people: [], forum: [] });
      }

      // Get last visit times for item-level "new" detection
      const visitTimesResult = await getUserLastVisitTimes(user.uid);
      if (visitTimesResult.success) {
        setLastVisitTimes(visitTimesResult.data);
      }

      return { success: result.success, counts: result.counts };

    } catch (err) {
      console.error('Error loading notification counts:', err.message);
      
      // Implement retry logic for network errors
      if (retryCount < 3 && (err.code === 'unavailable' || err.code === 'deadline-exceeded')) {
        const retryDelay = Math.pow(2, retryCount) * 1000;
        
        setIsRetrying(true);
        setTimeout(() => {
          loadNotificationCounts(retryCount + 1);
        }, retryDelay);
        return { success: false, error: err.message, retrying: true };
      }
      
      setError(err.message);
      setCounts({ opportunities: 0, people: 0, forum: 0 });
      setNewItems({ opportunities: [], people: [], forum: [] });
      return { success: false, error: err.message };
    } finally {
      if (retryCount === 0) { // Only set loading to false on the initial attempt
        setLoading(false);
      }
      if (retryCount >= 3) { // Stop retrying after max attempts
        setIsRetrying(false);
      }
    }
  }, [user?.uid]);

  /**
   * Debounced version of loadNotificationCounts to prevent excessive API calls
   */
  const debouncedLoadCounts = useCallback((delay = 500) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      loadNotificationCounts();
    }, delay);
  }, [loadNotificationCounts]);



  /**
   * Mark a page as visited and update counts
   * @param {string} pageType - The page type ('opportunities', 'people', 'forum')
   */
  const markPageVisited = useCallback(async (pageType) => {
    if (!user?.uid || !pageType) {
      return;
    }

    try {
      const result = await updateLastVisitTime(user.uid, pageType);
      
      if (result.success) {
        // Clear the count and new items for this page type immediately
        setCounts(prev => ({ ...prev, [pageType]: 0 }));
        setNewItems(prev => ({ ...prev, [pageType]: [] }));
        
        // Update last visit times
        const visitTimesResult = await getUserLastVisitTimes(user.uid);
        if (visitTimesResult.success) {
          setLastVisitTimes(visitTimesResult.data);
        }
        
        // Refresh counts after marking page as visited to ensure accuracy
        setTimeout(() => {
          loadNotificationCounts();
        }, 500); // Small delay to ensure visit time is updated
      }
    } catch (err) {
      console.error('Error marking page as visited:', err.message);
    }
  }, [user?.uid]);

  /**
   * Manually clear notification count for a specific page type
   * @param {string} pageType - The page type ('opportunities', 'people', 'forum')
   */
  const clearNotificationCount = useCallback((pageType) => {
    if (!pageType) {
      return;
    }
    
    setCounts(prev => ({ ...prev, [pageType]: 0 }));
  }, []);



  /**
   * Clear new items for a specific page type
   * @param {string} pageType - The page type ('opportunities', 'people', 'forum')
   */
  const clearNewItems = useCallback((pageType) => {
    if (!pageType) {
      return;
    }
    
    setNewItems(prev => ({ ...prev, [pageType]: [] }));
  }, []);



  /**
   * Refresh notification counts manually
   */
  const refreshCounts = useCallback(async () => {
    return await loadNotificationCounts();
  }, [loadNotificationCounts]);

  /**
   * Refresh counts with debouncing for navigation events
   */
  const refreshCountsDebounced = useCallback((delay = 500) => {
    debouncedLoadCounts(delay);
  }, [debouncedLoadCounts]);

  /**
   * Handle window focus events for optional refresh
   */
  const handleWindowFocus = useCallback(() => {
    if (!focusRefreshEnabled.current || !user?.uid) {
      return;
    }

    const now = Date.now();
    const timeSinceLastRefresh = now - lastFocusRefresh.current;
    
    // Only refresh if it's been more than 30 seconds since last focus refresh
    if (timeSinceLastRefresh > 30000) {
      lastFocusRefresh.current = now;
      debouncedLoadCounts(1000); // 1 second delay
    }
  }, [user?.uid, debouncedLoadCounts]);

  /**
   * Enable or disable focus-based refresh
   */
  const setFocusRefreshEnabled = useCallback((enabled) => {
    focusRefreshEnabled.current = enabled;
  }, []);

  /**
   * Debug function to check synchronization between counts and visible badges
   * @param {string} pageType - The page type to debug
   * @param {number} visibleBadgeCount - Number of badges actually visible on page
   */
  const debugSynchronization = useCallback((pageType, visibleBadgeCount) => {
    const navigationCount = counts[pageType] || 0;
    const newItemsCount = newItems[pageType]?.length || 0;
    
    console.group(`${pageType.toUpperCase()} Synchronization Check`);
    console.log('Navigation count:', navigationCount);
    console.log('New items count:', newItemsCount);
    console.log('Visible badge count:', visibleBadgeCount);
    console.log('New items IDs:', newItems[pageType]);
    
    if (navigationCount !== visibleBadgeCount) {
      console.warn('SYNCHRONIZATION MISMATCH detected!');
      console.log('Expected badges:', navigationCount);
      console.log('Actual badges:', visibleBadgeCount);
      console.log('Difference:', navigationCount - visibleBadgeCount);
    } else {
      console.log('Synchronization looks good!');
    }
    
    if (navigationCount !== newItemsCount) {
      console.warn('COUNT vs NEW ITEMS MISMATCH detected!');
      console.log('Navigation count:', navigationCount);
      console.log('New items array length:', newItemsCount);
    }
    
    console.groupEnd();
  }, [counts, newItems]);



  // Load initial data when user changes
  useEffect(() => {
    if (user?.uid) {
      loadNotificationCounts();
    } else {
      setCounts({ opportunities: 0, people: 0, forum: 0 });
      setNewItems({ opportunities: [], people: [], forum: [] });
      setLastVisitTimes(null);
      setLoading(false);
    }
  }, [user?.uid, loadNotificationCounts]);

  // Set up focus-based refresh listener
  useEffect(() => {
    if (!user?.uid) {
      return;
    }
    
    // Add focus event listener with debouncing
    window.addEventListener('focus', handleWindowFocus);
    
    // Cleanup
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [user?.uid, handleWindowFocus]);



  return {
    counts,
    newItems,
    loading,
    error,
    isRetrying,
    markPageVisited,
    clearNewItems,
    refreshCounts,
    refreshCountsDebounced,
    lastVisitTimes,
    clearNotificationCount,
    setFocusRefreshEnabled,
    debugSynchronization // Debug function for troubleshooting
  };
};